import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setTokenGetter, setActor } from "../lib/api";

const AuthContext = createContext(null);
const ROLES_CLAIM = "https://blf.360live.events/roles";

export function AuthProvider({ children }) {
  const {
    isLoading, isAuthenticated, user,
    loginWithRedirect, logout: auth0Logout, getAccessTokenSilently,
  } = useAuth0();

  // Register the access-token provider SYNCHRONOUSLY during render (not in an
  // effect). Child effects run before parent effects, so an effect here would
  // set the getter AFTER children (e.g. MapPlanner) already fired their initial
  // data fetch — that fetch would then go out without a token and receive the
  // public/stripped payload. Setting it in render guarantees the getter exists
  // before any child mounts. getAccessTokenSilently returns the cached token
  // when a session exists and throws otherwise (caught by the axios interceptor).
  setTokenGetter(getAccessTokenSilently);
  if (user) setActor(user.name || user.nickname || "", user.email || "");

  const roles = useMemo(
    () => (((user && user[ROLES_CLAIM]) || []).map((r) => String(r).toLowerCase())),
    [user]
  );
  const isSuperAdmin = roles.includes("superadmin");
  const isAdmin = roles.includes("admin") || isSuperAdmin;
  const isTeam = roles.includes("team") || isAdmin;

  const login = useCallback(() => loginWithRedirect(), [loginWithRedirect]);
  const logout = useCallback(
    () => auth0Logout({ logoutParams: { returnTo: window.location.origin } }),
    [auth0Logout]
  );

  const value = useMemo(
    () => ({ user, roles, isAdmin, isSuperAdmin, isTeam, isAuthenticated, loading: isLoading, login, logout }),
    [user, roles, isAdmin, isSuperAdmin, isTeam, isAuthenticated, isLoading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
